import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountUserPermissionsForm, { AccountUserPermissionsFormRef } from './AccountUserPermissionsForm';
import PermissionsSearchField from './PermissionsSearchField';
import useSaveAccountUserPermissions from '@/hooks/useApi/useSaveAccountUserPermissions';
import useFetchAccountUserPermissions from '@/hooks/useApi/useFetchAccountUserPermissions';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';

import { Button } from 'UI';
import styles from './AccountUserPermissionsDrawer.module.scss';

import { SideDrawer, Box, BodyText, type AccountUserPermissions as AccountUserPermissionsDrawer } from 'vopay';

type FC = FunctionComponent<AccountUserPermissionsDrawerProps>;

type AccountUserPermissionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  emailAddress?: string;
  accountToken?: string;
};

const AccountUserPermissionsDrawer: FC = ({ isOpen, onClose, emailAddress, accountToken }) => {
  const { permissions, loading, fetchAccountUserPermissions } = useFetchAccountUserPermissions(accountToken);
  const { saveAccountUserPermissions } = useSaveAccountUserPermissions(accountToken);
  const formRef = useRef<AccountUserPermissionsFormRef | null>(null);
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserPermissions' });

  // Track form state for button disabling
  const [hasChanges, setHasChanges] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [allEnabled, setAllEnabled] = useState(false);
  const isSearching = searchTerm.trim().length > 0;

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!emailAddress || !isOpen) return;

      try {
        setLoading(true);
        await fetchAccountUserPermissions({ EmailAddress: emailAddress });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.fetchPermissions');
        alertDialog.error(t('errors.title'), errorMessage);
        console.error('Error fetching permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [emailAddress, isOpen]);

  const handleSave = async (values: Record<string, boolean>) => {
    if (!emailAddress) return;

    try {
      setLoading(true);
      const params = { EmailAddress: emailAddress, ...(values as AccountUserPermissionsDrawer) };
      await saveAccountUserPermissions(params);
      onClose();
      toast.success(t('success.permissionsUpdated'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('errors.savePermissions');
      alertDialog.error(t('errors.title'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAll = (enabled: boolean) => {
    if (formRef.current) {
      formRef.current.toggleAll(enabled);
    }
  };

  const handleFromStateChange = (hasChanges: boolean, isValid: boolean) => {
    setHasChanges(hasChanges);
    setIsValid(isValid);
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col">
          <span>{t('drawer.title')}</span>
          {emailAddress && <BodyText weight="md">{emailAddress}</BodyText>}
        </Box>
      }
      footer={
        <Box jc="fs" gap="sm" className={styles.footer}>
          {!isSearching && (
            <Button
              onClick={() => handleToggleAll(!allEnabled)}
              colorScheme="secondary"
              className={styles.enableAllButton}
            >
              {allEnabled
                ? t('drawer.disableAllButton', { defaultValue: 'Disable All' })
                : t('drawer.enableAllButton', { defaultValue: 'Enable All' })}
            </Button>
          )}
          <Button
            disabled={loading || !hasChanges || !isValid}
            colorScheme="primary"
            className={styles.saveButton}
            form="accountUserPermissionsForm"
            type="submit"
          >
            {t('drawer.saveButton')}
          </Button>
        </Box>
      }
    >
      {!loading && (
        <>
          <PermissionsSearchField value={searchTerm} onChange={setSearchTerm} />
          <AccountUserPermissionsForm
            ref={formRef}
            id="accountUserPermissionsForm"
            searchTerm={searchTerm}
            onSubmit={handleSave}
            permissions={permissions}
            onFormStateChange={handleFromStateChange}
            onAllEnabledChange={setAllEnabled}
          />
        </>
      )}
    </SideDrawer>
  );
};

export default AccountUserPermissionsDrawer;
